import {type CSSProperties, useMemo, useState} from 'react'
import {StyleSheet, View} from 'react-native'
import {Trans} from '@lingui/react/macro'

import {cleanError} from '#/lib/strings/errors'
import {useAgent, useSession, useSessionApi} from '#/state/session'
import {agentToSessionAccount} from '#/state/session/agent'
import {useLoggedOutViewControls} from '#/state/shell/logged-out'
import {atoms as a, useTheme} from '#/alf'
import {FormError} from '#/components/forms/FormError'
import {Text} from '#/components/Typography'
import {QrSessionBlock} from '../SignUpWizard/QrSessionBlock'
import {type QrSessionInitResult} from '../SignUpWizard/useQrSession'
import {FormContainer} from './FormContainer'

export function CombinedFlow({
  error,
  onPressBack,
  widInitialData,
}: {
  error: string
  onPressBack: () => void
  widInitialData: QrSessionInitResult
}) {
  const t = useTheme()
  const agent = useAgent()
  const {currentAccount} = useSession()
  const {logoutEveryAccount, removeAccount} = useSessionApi()
  const {setShowLoggedOut} = useLoggedOutViewControls()
  const account = useMemo(() => agentToSessionAccount(agent), [agent])

  const [confirmCode, setConfirmCode] = useState('')
  const [password, setPassword] = useState('')
  const [isEmailProcessing, setIsEmailProcessing] = useState(false)
  const [emailError, setEmailError] = useState('')

  const onLogout = () => {
    if (account) removeAccount(account)
    logoutEveryAccount('Settings')
    setShowLoggedOut(true)
  }

  const onPressConfirmDelete = async () => {
    if (!currentAccount?.did) return
    setEmailError('')
    setIsEmailProcessing(true)
    try {
      // await agent.api.chat.bsky.actor.deleteAccount(undefined, {
      //   headers: DM_SERVICE_HEADERS,
      // })
      await agent.com.atproto.server.deleteAccount({
        did: currentAccount.did,
        password,
        token: confirmCode.replace(/\s/g, ''),
      })
      onLogout()
    } catch (e: unknown) {
      setEmailError(cleanError(e))
    }
    setIsEmailProcessing(false)
  }

  return (
    <FormContainer
      testID="loginForm"
      titleText={<Trans>Sign in</Trans>}
      style={{backgroundColor: 'transparent'}}>
      <View style={[{maxWidth: 300, width: '100%', alignSelf: 'center'}]}>
        <Text
          style={[
            a.text_sm,
            a.font_medium,
            t.atoms.text_contrast_medium,
            a.mb_sm,
            styles.labelText,
          ]}>
          <Trans>Remove user</Trans>
        </Text>

        {/* WID QR section */}
        <View style={[a.align_center, a.mb_md]}>
          <QrSessionBlock
            size={300}
            label={
              <Text>
                <Trans>Scan QR code with WID app</Trans>
              </Text>
            }
            queryKey="remove-user-combined"
            initFn={(signal, prevServiceId, prevSessionId) =>
              agent.account.requestDeleteAccount(
                {
                  ...(prevServiceId && {serviceId: prevServiceId}),
                  ...(prevSessionId && {sessionId: prevSessionId}),
                },
                {signal},
              )
            }
            initialData={widInitialData}
            statusFn={(params, signal) =>
              agent.quicklogin.status(params, {signal})
            }
            onCompleted={data => {
              if (!data.approvalToken) return
              void agent.account
                .deleteAccount({approvalToken: data.approvalToken})
                .then(r => {
                  if (r.success) onLogout()
                })
                .catch(() => {})
            }}
            autoRefresh
          />
        </View>

        {/* Divider */}
        <View style={styles.dividerRow}>
          <View
            style={[
              styles.dividerLine,
              {backgroundColor: t.palette.contrast_100},
            ]}
          />
          <Text style={[a.text_xs, t.atoms.text_contrast_low, a.px_sm]}>
            <Trans>or</Trans>
          </Text>
          <View
            style={[
              styles.dividerLine,
              {backgroundColor: t.palette.contrast_100},
            ]}
          />
        </View>

        {/* Email section */}
        <View style={[a.align_center, a.mt_md, a.gap_sm]}>
          <Text
            style={[
              a.text_xs,
              t.atoms.text_contrast_medium,
              {textAlign: 'center'},
            ]}>
            <Trans>Enter the confirmation code sent to your email:</Trans>
          </Text>
          <View style={[{width: '100%', maxWidth: 300}]}>
            <Text
              style={[
                a.text_xs,
                t.atoms.text_contrast_medium,
                styles.fieldLabel,
              ]}>
              <Trans>Confirmation code</Trans>
            </Text>
            <input
              type="text"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              name="delete-confirm-token"
              value={confirmCode}
              onChange={e => setConfirmCode(e.target.value)}
              style={inputStyle}
            />
          </View>
          <View style={[{width: '100%', maxWidth: 300}]}>
            <Text
              style={[
                a.text_xs,
                t.atoms.text_contrast_medium,
                styles.fieldLabel,
              ]}>
              <Trans>Password</Trans>
            </Text>
            <input
              type="password"
              autoComplete="new-password"
              name="delete-confirm-password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              style={inputStyle}
            />
          </View>
          {emailError ? (
            <Text
              style={[
                a.text_xs,
                {color: t.palette.negative_500, textAlign: 'center'},
              ]}>
              {emailError}
            </Text>
          ) : null}
          <button
            className={'BackButton'}
            onClick={() => {
              void onPressConfirmDelete()
            }}
            disabled={isEmailProcessing || !confirmCode || !password}
            style={{
              opacity: isEmailProcessing || !confirmCode || !password ? 0.6 : 1,
            }}>
            {isEmailProcessing ? (
              <Trans>Deleting...</Trans>
            ) : (
              <Trans>Delete account</Trans>
            )}
          </button>
        </View>
      </View>

      <FormError error={error} />
      <View style={[a.flex_row, a.align_center, a.pt_md, a.self_center]}>
        <button className={'BackButton'} onClick={onPressBack}>
          <Trans>Back</Trans>
        </button>
        <View style={a.flex_1} />
      </View>
    </FormContainer>
  )
}

const inputStyle: CSSProperties = {
  backgroundColor: 'rgba(60, 48, 76, 0.3)',
  borderRadius: '10px',
  fontSize: '16px',
  padding: '10px 20px',
  border: '1px solid #3c304c',
  color: '#a6a0ac',
  width: '100%',
  boxSizing: 'border-box',
  outline: 'none',
}

const styles = StyleSheet.create({
  fieldLabel: {
    marginBottom: 4,
    marginLeft: 4,
  },
  labelText: {
    textAlign: 'center',
    fontSize: 16,
  },
  dividerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginVertical: 16,
    paddingHorizontal: 20,
  },
  dividerLine: {
    flex: 1,
    height: 1,
  },
})
